// /appv3/socialPostIntents.tsx — shared constants + card component for
// V3 Social Post (Jun 15).
//
// Imported by /appv3/page.tsx (the V3 Social Post pre-step picker — same
// architecture as carouselTypes.tsx + Carousel V3's type pre-step). The
// intent picked here is sent to backend's /api/wizard/ai-generate as the
// `intent` form field and routed through INTENT_SCAFFOLDS in
// helpers/ad_brief.py — it shapes the LLM concept writer's composition +
// headline tone + CTA presence + visual style for the generated ad.
//
// User flow: pick product → pick intent → Generate.
// 1:1 only in v1 (matches Product Shot V3 + Carousel V3 conventions).
// 6 intents (4 locked + social-proof + problem-promise, user-locked Jun 15).

import type React from 'react'

// ─────────────────────────────────────────────────────────────────────
// Intent constants
// ─────────────────────────────────────────────────────────────────────

export type SocialPostIntentId =
  | 'sell'
  | 'awareness'
  | 'compare'
  | 'why-buy'
  | 'social-proof'
  | 'problem-promise'

export interface SocialPostIntent {
  id: SocialPostIntentId
  label: string
  /** One-line description — surfaced under the title in the intent card
   *  AND used as a header hint after the user picks one. */
  description: string
  /** Tiny eyebrow on the intent card, above the title. */
  eyebrow?: string
}

export const SOCIAL_POST_INTENTS: readonly SocialPostIntent[] = [
  {
    id: 'sell',
    label: 'Sell the product',
    description: 'Direct response — hero product + price/promo + strong CTA.',
    eyebrow: 'Conversion',
  },
  {
    id: 'awareness',
    label: 'Brand awareness',
    description: 'Editorial top-of-funnel reveal — mood-led, no hard CTA.',
    eyebrow: 'Discovery',
  },
  {
    id: 'compare',
    label: 'Compare product',
    description: 'Split composition — generic vs your product, you win.',
    eyebrow: 'Comparison',
  },
  {
    id: 'why-buy',
    label: 'Why buy this',
    description: 'Benefits-stacked infographic — product + 2-4 callouts.',
    eyebrow: 'Benefits',
  },
  {
    id: 'social-proof',
    label: 'Social proof',
    description: 'Customer quote + ★★★★★ — testimonial-led, authentic feel.',
    eyebrow: 'Testimonial',
  },
  {
    id: 'problem-promise',
    label: 'Problem → Promise',
    description: 'Two-state contrast — pain point on top, your fix below.',
    eyebrow: 'Transformation',
  },
] as const

export function getSocialPostIntent(id: SocialPostIntentId): SocialPostIntent {
  return SOCIAL_POST_INTENTS.find((i) => i.id === id) ?? SOCIAL_POST_INTENTS[0]
}

// ─────────────────────────────────────────────────────────────────────
// IntentCard — vertical TILE adapted from My Creatives' SectionHero
// pattern (gallery/page.tsx) + Carousel Type cards (carouselTypes.tsx).
// Same gradient backdrop + ambient blurred preview, but with a single
// big sample image instead of a fan deck — Social Post output is ONE
// image, so showing one example reads more honestly than fanned 4.
//
// Hover lifts the WHOLE card (scale + translateY + shadow). Selected
// state: pink border + scale + checkmark.
//
// Sample image: /social-post-intent-previews/<intent-id>/sample.png
// (drop one image per intent here; the card renders a soft surface
// panel if missing).
// ─────────────────────────────────────────────────────────────────────

export interface IntentCardProps {
  intent: SocialPostIntent
  selected: boolean
  onClick: () => void
  size?: 'large' | 'compact'
}

export function IntentCard({
  intent, selected, onClick, size = 'large',
}: IntentCardProps): React.ReactElement {
  const isLarge = size === 'large'

  // Size tokens — large mirrors Carousel Type cards' typography.
  const eyebrowSize = isLarge ? 'text-[9px]' : 'text-[8px]'
  const titleSize = isLarge ? 'text-xl lg:text-2xl' : 'text-base'
  const descSize = isLarge ? 'text-xs' : 'text-2xs'
  const titlePadding = isLarge ? 'px-5 pt-5' : 'px-3.5 pt-3.5'
  const samplePadding = isLarge ? 'px-5 pb-5' : 'px-3.5 pb-3.5'

  const samplePath = `/social-post-intent-previews/${intent.id}/sample.png`

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        group relative w-full aspect-[3/4]
        rounded-2xl overflow-hidden text-left cursor-pointer
        bg-gradient-to-br from-surface via-white to-surface-dark/30
        border-2 transition-all duration-300 ease-out
        ${selected
          ? 'border-primary scale-[1.02] shadow-xl shadow-primary/25'
          : 'border-surface-dark/30 hover:border-primary/40 hover:-translate-y-1 hover:scale-[1.02] hover:shadow-xl hover:shadow-dark/10'}
      `}
    >
      {/* Ambient backdrop — blurred sample as mood layer. Falls back
          gracefully to the gradient when the sample doesn't exist. */}
      <div
        className="absolute inset-0 opacity-25 blur-3xl scale-110"
        style={{
          backgroundImage: `url('${samplePath}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        aria-hidden="true"
      />

      {/* Vertical content stack: title block on top, sample image below. */}
      <div className="relative z-10 h-full flex flex-col">
        {/* Title block — keeps horizontal padding for readability. */}
        <div className={`flex-shrink-0 ${titlePadding}`}>
          <p className={`${eyebrowSize} font-bold uppercase tracking-[0.18em] text-dark-muted mb-1`}>
            {intent.eyebrow ?? 'Social post intent'}
          </p>
          <p className={`font-display font-bold leading-none ${titleSize} ${selected ? 'text-primary' : 'text-dark'}`}>
            {intent.label}
          </p>
          <p className={`${descSize} text-dark-muted mt-1.5 leading-snug line-clamp-2`}>
            {intent.description}
          </p>
        </div>

        {/* Sample image — single large preview. CSS background-image so
            missing files degrade to a soft surface panel (no broken
            image icon). */}
        <div className={`relative flex-1 ${samplePadding} mt-3`}>
          <div
            className="w-full h-full rounded-xl overflow-hidden border-[3px] border-white shadow-lg bg-surface/60"
            style={{
              backgroundImage: `url('${samplePath}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
            aria-hidden="true"
          />
        </div>
      </div>

      {/* Selected check badge */}
      {selected && (
        <div className={`absolute ${isLarge ? 'top-3 right-3 w-7 h-7' : 'top-2 right-2 w-5 h-5'} rounded-full bg-primary text-white flex items-center justify-center shadow-md z-20`}>
          <i className={`ri-check-line ${isLarge ? 'text-sm' : 'text-xs'}`} />
        </div>
      )}
    </button>
  )
}
